/**
 * SP Importadora — Busca preditiva do header.
 * Atua sobre [data-sp-search-input] e renderiza resultados em [data-sp-search-dropdown].
 * Destaca SKU quando o termo parece ser um código (alfanumérico com dígitos).
 */
(function () {
  'use strict';

  var DEBOUNCE_MS   = 280;
  var MIN_CHARS     = 2;
  var MAX_RESULTS   = 6;
  var SEARCH_URL    = '/search/suggest.json';
  var SKU_RE        = /^[A-Za-z0-9].*\d/;

  var searchInput    = document.querySelector('[data-sp-search-input]');
  var dropdown       = document.querySelector('[data-sp-search-dropdown]');
  var searchForm     = document.querySelector('[data-sp-search-form]');

  if (!searchInput || !dropdown) return;

  var debounceTimer  = null;
  var currentQuery   = '';
  var isOpen         = false;
  var abortController = null;

  /* ── Helpers ──────────────────────────────────── */

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function highlightTerm(text, term) {
    if (!term) return escapeHtml(text);
    var re = new RegExp('(' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
    return escapeHtml(text).replace(re, '<mark class="sp-search-highlight">$1</mark>');
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  function isSku(term) { return SKU_RE.test(term); }

  /* ── Open / close ─────────────────────────────── */

  function openDropdown() {
    dropdown.removeAttribute('hidden');
    dropdown.setAttribute('aria-hidden', 'false');
    searchInput.setAttribute('aria-expanded', 'true');
    isOpen = true;
  }

  function closeDropdown() {
    dropdown.setAttribute('hidden', '');
    dropdown.setAttribute('aria-hidden', 'true');
    searchInput.setAttribute('aria-expanded', 'false');
    isOpen = false;
  }

  /* ── Render ───────────────────────────────────── */

  function renderLoading() {
    openDropdown();
    dropdown.innerHTML = '<div class="sp-search-dropdown__loading" role="status" aria-live="polite">Buscando…</div>';
  }

  function renderEmpty(term) {
    openDropdown();
    var waMsg = encodeURIComponent('Olá! Busquei por "' + term + '" e não encontrei. Podem me ajudar?');
    var waNum = (window.spConfig && window.spConfig.whatsappNumber) ? window.spConfig.whatsappNumber : '';
    var waLink = waNum
      ? '<a href="https://wa.me/' + waNum + '?text=' + waMsg + '" class="sp-search-dropdown__wa" target="_blank" rel="noopener noreferrer">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>' +
        'Perguntar via WhatsApp</a>'
      : '';
    dropdown.innerHTML =
      '<div class="sp-search-dropdown__empty">' +
        '<p>Nenhum resultado para <strong>' + escapeHtml(term) + '</strong></p>' +
        waLink +
      '</div>';
  }

  function renderResults(results, term) {
    openDropdown();
    var skuSearch = isSku(term);

    var items = results.slice(0, MAX_RESULTS).map(function (product) {
      var title     = product.title || '';
      var url       = product.url   || '#';
      var sku       = (product.variants && product.variants[0] && product.variants[0].sku) ? product.variants[0].sku : '';
      var priceMin  = product.price_min;
      var imageUrl  = product.featured_image && product.featured_image.url ? product.featured_image.url + '&width=72' : '';

      var titleHtml = skuSearch
        ? escapeHtml(title)
        : highlightTerm(title, term);

      var skuHtml = sku
        ? '<span class="sp-search-dropdown__sku sp-mono">' + (skuSearch ? highlightTerm(sku, term) : escapeHtml(sku)) + '</span>'
        : '';

      var imgHtml = imageUrl
        ? '<img src="' + escapeHtml(imageUrl) + '" alt="" width="48" height="48" loading="lazy" class="sp-search-dropdown__img">'
        : '<div class="sp-search-dropdown__img-placeholder" aria-hidden="true"></div>';

      var priceHtml = priceMin != null
        ? '<span class="sp-search-dropdown__price">' + formatMoney(priceMin) + '</span>'
        : '';

      return '<a href="' + escapeHtml(url) + '" class="sp-search-dropdown__item">' +
        imgHtml +
        '<span class="sp-search-dropdown__meta">' +
          '<span class="sp-search-dropdown__title">' + titleHtml + '</span>' +
          skuHtml +
        '</span>' +
        priceHtml +
      '</a>';
    }).join('');

    var seeAllUrl = '/search?type=product&q=' + encodeURIComponent(term);

    dropdown.innerHTML =
      '<div class="sp-search-dropdown__list" role="listbox">' + items + '</div>' +
      '<a href="' + seeAllUrl + '" class="sp-search-dropdown__see-all">Ver todos os resultados para &ldquo;' + escapeHtml(term) + '&rdquo;</a>';
  }

  /* ── Fetch ────────────────────────────────────── */

  function fetchSuggestions(term) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    var url = SEARCH_URL + '?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=' + MAX_RESULTS;

    fetch(url, { signal: abortController.signal })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var products = data && data.resources && data.resources.results && data.resources.results.products
          ? data.resources.results.products
          : [];
        if (products.length > 0) {
          renderResults(products, term);
        } else {
          renderEmpty(term);
        }
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') closeDropdown();
      });
  }

  /* ── Event listeners ──────────────────────────── */

  searchInput.addEventListener('input', function () {
    var term = searchInput.value.trim();
    if (term === currentQuery) return;
    currentQuery = term;

    clearTimeout(debounceTimer);

    if (term.length < MIN_CHARS) {
      closeDropdown();
      return;
    }

    renderLoading();
    debounceTimer = setTimeout(function () { fetchSuggestions(term); }, DEBOUNCE_MS);
  });

  searchInput.addEventListener('focus', function () {
    if (currentQuery.length >= MIN_CHARS && !isOpen) {
      fetchSuggestions(currentQuery);
    }
  });

  /* Close on outside click */
  document.addEventListener('click', function (e) {
    if (!searchForm || (!searchForm.contains(e.target) && !dropdown.contains(e.target))) {
      closeDropdown();
    }
  });

  /* Close on Escape */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) {
      closeDropdown();
      searchInput.focus();
    }
  });

  /* Keyboard nav inside dropdown */
  searchInput.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    var items = dropdown.querySelectorAll('.sp-search-dropdown__item');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[0].focus();
    }
  });

  dropdown.addEventListener('keydown', function (e) {
    var items = Array.from(dropdown.querySelectorAll('.sp-search-dropdown__item, .sp-search-dropdown__see-all'));
    var idx   = items.indexOf(document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (idx < items.length - 1) items[idx + 1].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx > 0) items[idx - 1].focus();
      else searchInput.focus();
    } else if (e.key === 'Escape') {
      closeDropdown();
      searchInput.focus();
    }
  });

})();
